import React from 'react';
import { Map as MapIcon, LayoutGrid, AlertTriangle, Database } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../UI/tabs';
import { useLocale } from '../../contexts/LocaleContext';
import {
  TENANT_PAGE_TABS,
  TENANT_DATA_HUB_TAB,
  TENANT_DATA_IMPORT_SECTION,
  getNodeTabByKey,
} from '../../config/tenantNodeTabsConfig';
import TenantSupplyChainMap from './TenantSupplyChainMap';
import TenantOverviewPanel from './TenantOverviewPanel';
import TenantAnalysisPanel from './TenantAnalysisPanel';
import TenantSuppliersPanel from './TenantSuppliersPanel';
import TenantSupplyChainDataLayout from './TenantSupplyChainDataLayout';

const PAGE_TAB_ICONS = {
  map: MapIcon,
  overview: LayoutGrid,
  analysis: AlertTriangle,
  [TENANT_DATA_HUB_TAB]: Database,
};

function TenantCompanyPageTabs({
  activePrimaryTab,
  onPrimaryTabChange,
  activeDataSection,
  onDataSectionChange,
  onNavigate,
  profile,
  hasTenantProfile,
  nodesByKey,
  tenantSuppliers,
  companies = [],
  loadingSuppliers,
  savingSuppliers,
  loadingProfile,
  savingProfile,
  loadingByKey,
  savingByKey,
  uploadingByKey,
  resultsByKey,
  existingImportByKey,
  mapData,
  mapDataLoading = false,
  mapEnhancing = false,
  selectedMarkerId,
  onMarkerSelect,
  overallRiskScore,
  onSaveProfile,
  onSaveNodeRows,
  onUploadFromPanel,
  onSuppliersChange,
  onTenantAnalysisComplete,
}) {
  const { t } = useLocale();

  const goToDataImport = () => {
    onNavigate?.({ primary: TENANT_DATA_HUB_TAB, dataSection: TENANT_DATA_IMPORT_SECTION });
  };

  const goToDataNode = (nodeKey) => {
    const tab = getNodeTabByKey(nodeKey);
    if (!tab) return;
    onNavigate?.({ primary: TENANT_DATA_HUB_TAB, dataSection: tab.pageTab });
  };

  const handleMarkerSelect = (markerId, options) => {
    if (options?.openNodeTab) {
      goToDataNode(options.openNodeTab);
    }
    onMarkerSelect?.(markerId);
  };

  return (
    <Tabs value={activePrimaryTab} onValueChange={onPrimaryTabChange} className="w-full">
      <TabsList variant="pill" className="mb-4 w-full flex-wrap justify-start">
        {TENANT_PAGE_TABS.map((tab) => {
          const Icon = PAGE_TAB_ICONS[tab.key];
          return (
            <TabsTrigger key={tab.key} value={tab.key} variant="pill" icon={Icon}>
              {t(tab.labelKey)}
            </TabsTrigger>
          );
        })}
      </TabsList>

      <TabsContent value="map" className="mt-0">
        <TenantSupplyChainMap
          markers={mapData.markers}
          routes={mapData.routes}
          bounds={mapData.bounds}
          loading={mapDataLoading}
          enhancing={mapEnhancing}
          selectedMarkerId={selectedMarkerId}
          onMarkerSelect={handleMarkerSelect}
          onGoToDataImport={goToDataImport}
          overallRiskScore={overallRiskScore}
          profile={profile}
          tenantSuppliers={tenantSuppliers}
          companies={companies}
        />
      </TabsContent>

      <TabsContent value="overview" className="mt-0">
        <div className="flex flex-col gap-6">
          <TenantOverviewPanel
            profile={profile}
            saving={savingProfile}
            loading={loadingProfile}
            nodesByKey={nodesByKey}
            supplierCount={tenantSuppliers?.length ?? 0}
            onSaveProfile={onSaveProfile}
            onOpenDataSection={(section) =>
              onNavigate?.({ primary: TENANT_DATA_HUB_TAB, dataSection: section })
            }
          />
          <TenantSuppliersPanel
            suppliers={tenantSuppliers}
            loading={loadingSuppliers}
            saving={savingSuppliers}
            onSuppliersChange={onSuppliersChange}
          />
        </div>
      </TabsContent>

      <TabsContent value="analysis" className="mt-0">
        <TenantAnalysisPanel
          hasTenantProfile={hasTenantProfile}
          tenantSuppliers={tenantSuppliers}
          onGoToOverview={() => onPrimaryTabChange('overview')}
          onAnalysisComplete={onTenantAnalysisComplete}
        />
      </TabsContent>

      <TabsContent value={TENANT_DATA_HUB_TAB} className="mt-0">
        <TenantSupplyChainDataLayout
          activeDataSection={activeDataSection}
          onDataSectionChange={onDataSectionChange}
          nodesByKey={nodesByKey}
          loadingByKey={loadingByKey}
          savingByKey={savingByKey}
          uploadingByKey={uploadingByKey}
          resultsByKey={resultsByKey}
          existingImportByKey={existingImportByKey}
          onSaveNodeRows={onSaveNodeRows}
          onUploadFromPanel={onUploadFromPanel}
        />
      </TabsContent>
    </Tabs>
  );
}

export default TenantCompanyPageTabs;
